import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  useTheme,
  Button,
  ButtonGroup,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Grid,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as WalletIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SyncIcon from "@mui/icons-material/Sync";
import PrintIcon from "@mui/icons-material/Print";
import Header from "components/Header";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import formatRupiah from "utils/formatRupiah";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { fetchListTransaksiWarkat } from "state/alokasiAction";
import { useDispatch, useSelector } from "react-redux";
import { fetchSaveWarkat } from "state/setoranAction";
import moment from "moment";
import { setMessage, setStatus } from "state";
import FloatingAlert from "components/FloatingAlert";
moment.locale("id");

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);
const BukuWarkat = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [grouping, setGrouping] = useState("harian"); // harian, bulanan, tahunan
  const [startDate, setStartDate] = useState(moment().subtract(6, "days"));
  const [endDate, setEndDate] = useState(moment());
  const [expandedRows, setExpandedRows] = useState({});
  const { status, message } = useSelector((state) => state.global);
  const { list_transaksi_warkat } = useSelector((state) => state.alokasi);

  const getInit = () => {
    dispatch(fetchListTransaksiWarkat());
  };
  console.log("LIST TRANSAKSI WARKAT:", list_transaksi_warkat);

  useEffect(() => {
    getInit();
  }, []);

  useEffect(() => {
    if (grouping === "harian") {
      setStartDate(moment().subtract(6, "days"));
      setEndDate(moment());
    } else if (grouping === "bulanan") {
      setStartDate(moment().subtract(5, "months"));
      setEndDate(moment());
    } else if (grouping === "tahunan") {
      setStartDate(moment().subtract(4, "years"));
      setEndDate(moment());
    }
  }, [grouping]);

  const handleCloseSnackbar = () => {
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
    getInit();
  };

  const currentData = useMemo(() => {
    if (!startDate || !endDate) return [];
    if (!list_transaksi_warkat || list_transaksi_warkat.length === 0) return [];

    const start = moment(startDate);
    const end = moment(endDate);

    // Safety check
    if (end.isBefore(start)) return [];

    // Only process items with status 'realisasi'
    const realisasiItems = list_transaksi_warkat.filter(
      (item) => item.status === "realisasi",
    );

    // Determine grouping key format based on mode
    const getGroupKey = (dateStr) => {
      const m = moment(dateStr);
      if (grouping === "harian") return m.format("YYYY-MM-DD");
      if (grouping === "bulanan") return m.format("YYYY-MM");
      if (grouping === "tahunan") return m.format("YYYY");
      return m.format("YYYY-MM-DD");
    };

    // Format label for display
    const getLabel = (key) => {
      if (grouping === "harian")
        return moment(key, "YYYY-MM-DD").format("DD MMM YYYY");
      if (grouping === "bulanan")
        return moment(key, "YYYY-MM").format("MMM YYYY");
      if (grouping === "tahunan") return key;
      return key;
    };

    // Group realisasi items by tanggal_setoran
    const grouped = {};
    realisasiItems.forEach((item) => {
      if (!item.tanggal_setoran) return;
      const itemDate = moment(item.tanggal_setoran);

      // Filter by date range
      if (
        grouping === "harian" &&
        (itemDate.isBefore(start, "day") || itemDate.isAfter(end, "day"))
      )
        return;
      if (
        grouping === "bulanan" &&
        (itemDate.isBefore(start, "month") || itemDate.isAfter(end, "month"))
      )
        return;
      if (
        grouping === "tahunan" &&
        (itemDate.isBefore(start, "year") || itemDate.isAfter(end, "year"))
      )
        return;

      const key = getGroupKey(item.tanggal_setoran);
      if (!grouped[key]) {
        grouped[key] = { pemasukan: 0, pengeluaran: 0, items: [] };
      }

      const nominal = Number(item.nominal || 0);
      if (item.tipe === "pemasukan") {
        grouped[key].pemasukan += nominal;
      } else if (item.tipe === "pengeluaran") {
        grouped[key].pengeluaran += nominal;
      }
      grouped[key].items.push(item);
    });

    // Also include ALL items (not just realisasi) in the items list for reference
    list_transaksi_warkat.forEach((item) => {
      if (item.status === "realisasi") return; // already added
      if (!item.tanggal_setoran) return;
      const itemDate = moment(item.tanggal_setoran);

      if (
        grouping === "harian" &&
        (itemDate.isBefore(start, "day") || itemDate.isAfter(end, "day"))
      )
        return;
      if (
        grouping === "bulanan" &&
        (itemDate.isBefore(start, "month") || itemDate.isAfter(end, "month"))
      )
        return;
      if (
        grouping === "tahunan" &&
        (itemDate.isBefore(start, "year") || itemDate.isAfter(end, "year"))
      )
        return;

      const key = getGroupKey(item.tanggal_setoran);
      if (!grouped[key]) {
        grouped[key] = { pemasukan: 0, pengeluaran: 0, items: [] };
      }
      grouped[key].items.push(item);
    });

    // Build timeline with all periods in range (fill empty periods with 0)
    const data = [];

    if (grouping === "harian") {
      let current = start.clone();
      let count = 0;
      while (current.isSameOrBefore(end, "day") && count < 60) {
        const key = current.format("YYYY-MM-DD");
        const g = grouped[key] || { pemasukan: 0, pengeluaran: 0, items: [] };
        data.push({
          periode: getLabel(key),
          pemasukan: g.pemasukan,
          pengeluaran: g.pengeluaran,
          items: g.items,
        });
        current.add(1, "day");
        count++;
      }
    } else if (grouping === "bulanan") {
      let current = start.clone().startOf("month");
      const endMonth = end.clone().startOf("month");
      let count = 0;
      while (current.isSameOrBefore(endMonth, "month") && count < 60) {
        const key = current.format("YYYY-MM");
        const g = grouped[key] || { pemasukan: 0, pengeluaran: 0, items: [] };
        data.push({
          periode: getLabel(key),
          pemasukan: g.pemasukan,
          pengeluaran: g.pengeluaran,
          items: g.items,
        });
        current.add(1, "month");
        count++;
      }
    } else if (grouping === "tahunan") {
      let current = start.clone().startOf("year");
      const endYear = end.clone().startOf("year");
      let count = 0;
      while (current.isSameOrBefore(endYear, "year") && count < 20) {
        const key = current.format("YYYY");
        const g = grouped[key] || { pemasukan: 0, pengeluaran: 0, items: [] };
        data.push({
          periode: getLabel(key),
          pemasukan: g.pemasukan,
          pengeluaran: g.pengeluaran,
          items: g.items,
        });
        current.add(1, "year");
        count++;
      }
    }

    return data;
  }, [grouping, startDate, endDate, list_transaksi_warkat]);

  const handleCetakLaporan = () => {
    const periodeLabel = grouping.charAt(0).toUpperCase() + grouping.slice(1);
    const startLabel = startDate
      ? startDate.format(
          grouping === "harian"
            ? "DD"
            : grouping === "bulanan"
              ? "MMMM YYYY"
              : "YYYY",
        )
      : "-";
    const endLabel = endDate
      ? endDate.format(
          grouping === "harian"
            ? "DD"
            : grouping === "bulanan"
              ? "MMMM YYYY"
              : "YYYY",
        )
      : "-";
    const cetakDate = moment().format("DD, HH:mm");

    const fmtRp = (val) => {
      if (val === null || val === undefined) return "Rp 0";
      const neg = val < 0;
      const f = Math.abs(val).toLocaleString("id-ID", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return `${neg ? "(" : ""}Rp ${f}${neg ? ")" : ""}`;
    };

    const totalPemasukan = currentData.reduce((s, r) => s + r.pemasukan, 0);
    const totalPengeluaran = currentData.reduce((s, r) => s + r.pengeluaran, 0);
    const totalNet = totalPemasukan - totalPengeluaran;

    // Build summary table rows
    const summaryRows = currentData
      .filter((r) => r.pemasukan > 0 || r.pengeluaran > 0)
      .map(
        (r, i) => `
        <tr class="${i % 2 === 0 ? "even" : "odd"}">
          <td class="center">${i + 1}</td>
          <td>${r.periode}</td>
          <td class="right green">${fmtRp(r.pemasukan)}</td>
          <td class="right red">${fmtRp(r.pengeluaran)}</td>
          <td class="right bold ${r.pemasukan - r.pengeluaran >= 0 ? "green" : "red"}">${fmtRp(r.pemasukan - r.pengeluaran)}</td>
        </tr>`,
      )
      .join("");

    // Build detail sections per period (only periods with items)
    const detailSections = currentData
      .filter((r) => r.items && r.items.length > 0)
      .map((r) => {
        const itemRows = r.items
          .map(
            (item, i) => `
          <tr class="${i % 2 === 0 ? "even" : "odd"} ${item.status !== "realisasi" ? "muted" : ""}">
            <td class="center">${i + 1}</td>
            <td>${item.nama || "-"}</td>
            <td><span class="badge ${item.tipe === "pemasukan" ? "badge-green" : "badge-red"}">${item.tipe === "pemasukan" ? "Pemasukan" : "Pengeluaran"}</span></td>
            <td><span class="badge ${item.status === "realisasi" ? "badge-blue" : "badge-orange"}">${item.status === "realisasi" ? "Realisasi" : "Alokasi"}</span></td>
            <td>${item.kode_perkiraan ? `${item.kode_perkiraan} - ${item.nama_perkiraan || ""}` : "-"}</td>
            <td class="right ${item.tipe === "pemasukan" ? "green" : "red"} bold">${item.tipe === "pengeluaran" ? "- " : ""}${fmtRp(item.nominal)}</td>
          </tr>`,
          )
          .join("");

        const periodPemasukan = r.items
          .filter((it) => it.status === "realisasi" && it.tipe === "pemasukan")
          .reduce((s, it) => s + Number(it.nominal || 0), 0);
        const periodPengeluaran = r.items
          .filter(
            (it) => it.status === "realisasi" && it.tipe === "pengeluaran",
          )
          .reduce((s, it) => s + Number(it.nominal || 0), 0);

        return `
        <div class="detail-block">
          <div class="detail-header">
            <span class="detail-title">${r.periode}</span>
            <span class="detail-summary">
              <span class="green">+${fmtRp(periodPemasukan)}</span>&nbsp;&nbsp;
              <span class="red">-${fmtRp(periodPengeluaran)}</span>&nbsp;&nbsp;
              <strong class="${periodPemasukan - periodPengeluaran >= 0 ? "green" : "red"}">Net: ${fmtRp(periodPemasukan - periodPengeluaran)}</strong>
            </span>
          </div>
          <table class="detail-table">
            <thead>
              <tr>
                <th class="center" style="width:35px">No</th>
                <th>Nama Transaksi</th>
                <th style="width:90px">Tipe</th>
                <th style="width:80px">Status</th>
                <th>Kode Perkiraan</th>
                <th class="right" style="width:130px">Nominal</th>
              </tr>
            </thead>
            <tbody>${itemRows}</tbody>
          </table>
        </div>`;
      })
      .join("");

    const htmlContent = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Laporan Buku Warkat - ${periodeLabel}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm 10mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html { width: 210mm; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 10px; color: #1a1a2e; background: #fff; line-height: 1.4; width: 190mm; max-width: 190mm; margin: 0 auto; overflow-x: hidden; }

    /* HEADER */
    .header { text-align: center; padding: 14px 0 12px; border-bottom: 3px solid #16213e; margin-bottom: 14px; }
    .header h1 { font-size: 18px; color: #16213e; letter-spacing: 2px; margin-bottom: 3px; }
    .header .company { font-size: 12px; color: #0f3460; font-weight: 600; }
    .header .meta { font-size: 10px; color: #666; margin-top: 4px; }
    .header .meta span { margin: 0 6px; }

    /* SUMMARY CARDS */
    .summary-bar { display: flex; gap: 8px; margin-bottom: 14px; }
    .summary-card { flex: 1; border-radius: 6px; padding: 10px 12px; text-align: center; }
    .summary-card .label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; opacity: 0.85; margin-bottom: 3px; }
    .summary-card .value { font-size: 15px; font-weight: 800; }
    .card-pemasukan { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); color: #1b5e20; border-left: 4px solid #2e7d32; }
    .card-pengeluaran { background: linear-gradient(135deg, #ffebee, #ffcdd2); color: #b71c1c; border-left: 4px solid #c62828; }
    .card-net { background: linear-gradient(135deg, #e3f2fd, #bbdefb); color: #0d47a1; border-left: 4px solid #1565c0; }
    .card-net.negative { background: linear-gradient(135deg, #fce4ec, #f8bbd0); color: #880e4f; border-left-color: #ad1457; }

    /* MAIN TABLE */
    .section-title { font-size: 11px; font-weight: 700; color: #16213e; padding: 6px 0 4px; border-bottom: 2px solid #e0e0e0; margin-bottom: 0; text-transform: uppercase; letter-spacing: 1px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.main { margin-bottom: 14px; }
    table.main thead th { background: #16213e; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 6px; white-space: nowrap; }
    table.main tbody td { padding: 5px 6px; border-bottom: 1px solid #eee; font-size: 10px; word-wrap: break-word; overflow-wrap: break-word; }
    table.main tr.even { background: #fafafa; }
    table.main tr.odd { background: #fff; }
    table.main tfoot td { padding: 7px 6px; font-weight: 800; font-size: 10px; background: #f5f5f5; border-top: 2px solid #16213e; }

    /* DETAIL BLOCKS */
    .detail-block { margin-bottom: 10px; page-break-inside: avoid; }
    .detail-header { display: flex; justify-content: space-between; align-items: center; background: #16213e; color: #fff; padding: 5px 10px; border-radius: 4px 4px 0 0; }
    .detail-title { font-weight: 700; font-size: 10px; }
    .detail-summary { font-size: 9px; }
    table.detail-table { border: 1px solid #ddd; border-top: none; }
    table.detail-table thead th { background: #f0f0f0; color: #333; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.3px; padding: 4px 5px; border-bottom: 1px solid #ddd; white-space: nowrap; }
    table.detail-table tbody td { padding: 4px 5px; border-bottom: 1px solid #eee; font-size: 9.5px; word-wrap: break-word; overflow-wrap: break-word; }
    table.detail-table tr.even { background: #fafafa; }
    table.detail-table tr.muted { opacity: 0.55; }

    /* BADGES */
    .badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; }
    .badge-green { background: #e8f5e9; color: #2e7d32; }
    .badge-red { background: #ffebee; color: #c62828; }
    .badge-blue { background: #e3f2fd; color: #1565c0; }
    .badge-orange { background: #fff3e0; color: #e65100; }

    /* UTILITIES */
    .right { text-align: right; }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .green { color: #2e7d32; }
    .red { color: #c62828; }

    /* FOOTER */
    .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #ccc; text-align: center; font-size: 8px; color: #999; }

    @media print {
      html, body { width: 100%; max-width: 100%; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>LAPORAN BUKU WARKAT</h1>
    <div class="company">Prima Centre DC &mdash; Sukabumi</div>
    <div class="meta">
      <span>Periode: <strong>${periodeLabel}</strong></span>
      <span>|</span>
      <span>${startLabel} &mdash; ${endLabel}</span>
      <span>|</span>
      <span>Dicetak: ${cetakDate}</span>
    </div>
  </div>

  <div class="summary-bar">
    <div class="summary-card card-pemasukan">
      <div class="label">Total Pemasukan</div>
      <div class="value">${fmtRp(totalPemasukan)}</div>
    </div>
    <div class="summary-card card-pengeluaran">
      <div class="label">Total Pengeluaran</div>
      <div class="value">${fmtRp(totalPengeluaran)}</div>
    </div>
    <div class="summary-card card-net ${totalNet < 0 ? "negative" : ""}">
      <div class="label">Selisih (Net)</div>
      <div class="value">${fmtRp(totalNet)}</div>
    </div>
  </div>

  <p class="section-title">Ringkasan Per ${periodeLabel}</p>
  <table class="main">
    <thead>
      <tr>
        <th class="center" style="width:35px">No</th>
        <th>Periode</th>
        <th class="right">Pemasukan</th>
        <th class="right">Pengeluaran</th>
        <th class="right">Selisih (Net)</th>
      </tr>
    </thead>
    <tbody>
      ${summaryRows || '<tr><td colspan="5" class="center" style="padding:20px;color:#999">Tidak ada data untuk periode ini</td></tr>'}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="2">TOTAL</td>
        <td class="right green">${fmtRp(totalPemasukan)}</td>
        <td class="right red">${fmtRp(totalPengeluaran)}</td>
        <td class="right ${totalNet >= 0 ? "green" : "red"}">${fmtRp(totalNet)}</td>
      </tr>
    </tfoot>
  </table>

  ${detailSections ? `<p class="section-title">Detail Transaksi Per Periode</p>${detailSections}` : ""}

  <div class="footer">
    Dokumen ini dicetak secara otomatis oleh sistem Prima Centre &mdash; ${cetakDate}
  </div>

  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>`;

    const tab = window.open("", "_blank");
    if (tab) {
      tab.document.open();
      tab.document.write(htmlContent);
      tab.document.close();
    }
  };

  // Summary totals
  const totals = useMemo(() => {
    const pemasukan = currentData.reduce((s, r) => s + r.pemasukan, 0);
    const pengeluaran = currentData.reduce((s, r) => s + r.pengeluaran, 0);
    return { pemasukan, pengeluaran, net: pemasukan - pengeluaran };
  }, [currentData]);

  const statCardStyle = {
    p: 2.5,
    borderRadius: "16px",
    backgroundColor: theme.palette.background.alt,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  };

  const handleSyncSaldoAwal = async (item) => {
    const saldoAwal = Number(item.pemasukan) - Number(item.pengeluaran);
    const payloadSaldoAwal = {
      nama: "Saldo Awal",
      tanggal_kas: moment(item.periode, "DD MMM YYYY", "id")
        .add(1, "day")
        .format(),
      tanggal_setoran: moment(item.periode, "DD MMM YYYY", "id")
        .add(1, "day")
        .format(),
      tipe: "pemasukan",
      status: "realisasi",
      catatan: "Saldo Awal",
      nominal: saldoAwal,
    };

    try {
      await dispatch(fetchSaveWarkat(payloadSaldoAwal));
    } catch (error) {
      console.error("Error saving warkat:", error);
      alert("Terjadi kesalahan saat menyimpan data. Silakan coba lagi.");
    }
  };

  return (
    <Box m="1.5rem 2.5rem" pb="1px">
      <Header title="BUKU WARKAT" subtitle="Laporan dan Grafik Buku Warkat" />

      <Paper
        sx={{
          p: 2.5,
          mt: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 3,
          backgroundColor: theme.palette.background.alt,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            color={theme.palette.secondary[100]}
            mb={1.5}
            fontWeight="600"
          >
            Pilih Periode Laporan
          </Typography>
          <ButtonGroup
            variant="contained"
            disableElevation
            sx={{ borderRadius: "8px", overflow: "hidden" }}
          >
            <Button
              onClick={() => setGrouping("harian")}
              sx={{
                px: 3,
                py: 1,
                backgroundColor:
                  grouping === "harian"
                    ? theme.palette.secondary[500]
                    : theme.palette.primary.main,
                color:
                  grouping === "harian" ? "#fff" : theme.palette.secondary[200],
                fontWeight: grouping === "harian" ? "bold" : "500",
                "&:hover": {
                  backgroundColor:
                    grouping === "harian"
                      ? theme.palette.secondary[600]
                      : theme.palette.primary.light,
                },
              }}
            >
              Harian
            </Button>
            <Button
              onClick={() => setGrouping("bulanan")}
              sx={{
                px: 3,
                py: 1,
                backgroundColor:
                  grouping === "bulanan"
                    ? theme.palette.secondary[500]
                    : theme.palette.primary.main,
                color:
                  grouping === "bulanan"
                    ? "#fff"
                    : theme.palette.secondary[200],
                fontWeight: grouping === "bulanan" ? "bold" : "500",
                "&:hover": {
                  backgroundColor:
                    grouping === "bulanan"
                      ? theme.palette.secondary[600]
                      : theme.palette.primary.light,
                },
              }}
            >
              Bulanan
            </Button>
            <Button
              onClick={() => setGrouping("tahunan")}
              sx={{
                px: 3,
                py: 1,
                backgroundColor:
                  grouping === "tahunan"
                    ? theme.palette.secondary[500]
                    : theme.palette.primary.main,
                color:
                  grouping === "tahunan"
                    ? "#fff"
                    : theme.palette.secondary[200],
                fontWeight: grouping === "tahunan" ? "bold" : "500",
                "&:hover": {
                  backgroundColor:
                    grouping === "tahunan"
                      ? theme.palette.secondary[600]
                      : theme.palette.primary.light,
                },
              }}
            >
              Tahunan
            </Button>
          </ButtonGroup>
        </Box>

        <Box>
          <Typography
            variant="h6"
            color={theme.palette.secondary[100]}
            mb={1.5}
            fontWeight="600"
          >
            Rentang Waktu
          </Typography>
          <Box display="flex" gap="15px" alignItems="center">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={`Mulai ${grouping === "harian" ? "Tanggal" : grouping === "bulanan" ? "Bulan" : "Tahun"}`}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                maxDate={endDate}
                views={
                  grouping === "harian"
                    ? ["year", "month", "day"]
                    : grouping === "bulanan"
                      ? ["year", "month"]
                      : ["year"]
                }
                format={
                  grouping === "harian"
                    ? "DD/MM/YYYY"
                    : grouping === "bulanan"
                      ? "MM/YYYY"
                      : "YYYY"
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: 170,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": {
                          borderColor: theme.palette.secondary[500],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.secondary[500],
                          borderWidth: "1px",
                        },
                      },
                    },
                  },
                }}
              />
              <Typography variant="h6" color={theme.palette.secondary[300]}>
                -
              </Typography>
              <DatePicker
                label={`Sampai ${grouping === "harian" ? "Tanggal" : grouping === "bulanan" ? "Bulan" : "Tahun"}`}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                minDate={startDate}
                views={
                  grouping === "harian"
                    ? ["year", "month", "day"]
                    : grouping === "bulanan"
                      ? ["year", "month"]
                      : ["year"]
                }
                format={
                  grouping === "harian"
                    ? "DD/MM/YYYY"
                    : grouping === "bulanan"
                      ? "MM/YYYY"
                      : "YYYY"
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: 170,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": {
                          borderColor: theme.palette.secondary[500],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.secondary[500],
                          borderWidth: "1px",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box display="flex" alignItems="flex-end">
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleCetakLaporan}
            disabled={currentData.length === 0}
            sx={{
              px: 3,
              py: 1.2,
              fontWeight: "bold",
              textTransform: "none",
              fontSize: 14,
              borderRadius: "8px",
              backgroundColor: theme.palette.info.main,
              color: "#fff",
              "&:hover": { backgroundColor: theme.palette.info.dark },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.grey[700],
                color: theme.palette.grey[500],
              },
            }}
          >
            Cetak Laporan
          </Button>
        </Box>
      </Paper>

      {/* SUMMARY STAT CARDS */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              ...statCardStyle,
              borderTop: "4px solid #4caf50",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(76, 175, 80, 0.15)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                }}
              >
                <TrendingUpIcon sx={{ color: "#4caf50" }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color={theme.palette.text.secondary}
              >
                Total Pemasukan
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" color="#4caf50" mt={2}>
              {formatRupiah(totals.pemasukan)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              ...statCardStyle,
              borderTop: "4px solid #f44336",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(244, 67, 54, 0.15)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                }}
              >
                <TrendingDownIcon sx={{ color: "#f44336" }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color={theme.palette.text.secondary}
              >
                Total Pengeluaran
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold" color="#f44336" mt={2}>
              {formatRupiah(totals.pengeluaran)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              ...statCardStyle,
              borderTop: `4px solid ${totals.net >= 0 ? "#2196f3" : "#ff9800"}`,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 8px 24px ${totals.net >= 0 ? "rgba(33, 150, 243, 0.15)" : "rgba(255, 152, 0, 0.15)"}`,
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor:
                    totals.net >= 0
                      ? "rgba(33, 150, 243, 0.1)"
                      : "rgba(255, 152, 0, 0.1)",
                }}
              >
                <WalletIcon
                  sx={{ color: totals.net >= 0 ? "#2196f3" : "#ff9800" }}
                />
              </Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color={theme.palette.text.secondary}
              >
                Selisih (Net)
              </Typography>
            </Box>
            <Typography
              variant="h3"
              fontWeight="bold"
              color={totals.net >= 0 ? "#2196f3" : "#ff9800"}
              mt={2}
            >
              {formatRupiah(totals.net)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* CHART SECTION HEADER */}
      <Typography
        variant="h4"
        fontWeight="700"
        mt="2.5rem"
        mb="1rem"
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.secondary[100]}, ${theme.palette.secondary[400]})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "0px 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${theme.palette.secondary[500]}20, ${theme.palette.secondary[700]}30)`,
          }}
        >
          <BarChartIcon sx={{ color: theme.palette.secondary[300] }} />
        </Box>
        Grafik {grouping.charAt(0).toUpperCase() + grouping.slice(1)}
      </Typography>
      <Box>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: "20px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.08)",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(145deg, #1e1e1e, #2a2a2a)"
                : "#ffffff",
            p: 3,
          }}
        >
          <Box height="400px">
            <Bar
              data={{
                labels: currentData.map((d) => d.periode),
                datasets: [
                  {
                    label: "Pemasukan",
                    data: currentData.map((d) => d.pemasukan),
                    backgroundColor: "rgba(76, 175, 80, 0.75)",
                    borderRadius: 6,
                    borderSkipped: false,
                  },
                  {
                    label: "Pengeluaran",
                    data: currentData.map((d) => d.pengeluaran),
                    backgroundColor: "rgba(244, 67, 54, 0.75)",
                    borderRadius: 6,
                    borderSkipped: false,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      color: theme.palette.secondary[200],
                      callback: function (value) {
                        const label = this.getLabelForValue(value);
                        if (grouping === "harian") {
                          return moment(label, "DD MMM YYYY").format("DD");
                        }
                        return label;
                      },
                    },
                  },
                  y: {
                    grid: {
                      color: theme.palette.secondary[200] + "40", // slightly transparent grid lines
                    },
                    ticks: {
                      color: theme.palette.secondary[200],
                      callback: function (value) {
                        if (value >= 1e9)
                          return (
                            (value / 1e9).toLocaleString("id-ID", {
                              maximumFractionDigits: 1,
                            }) + " M"
                          );
                        if (value >= 1e6)
                          return (
                            (value / 1e6).toLocaleString("id-ID", {
                              maximumFractionDigits: 1,
                            }) + " Jt"
                          );
                        if (value >= 1e3)
                          return (
                            (value / 1e3).toLocaleString("id-ID", {
                              maximumFractionDigits: 1,
                            }) + " Rb"
                          );
                        return value.toLocaleString("id-ID");
                      },
                    },
                  },
                },
                plugins: {
                  legend: {
                    position: "top",
                    labels: {
                      color: theme.palette.secondary[200],
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return (
                          context.dataset.label +
                          ": " +
                          formatRupiah(context.raw)
                        );
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* TABLE SECTION HEADER */}
      <Typography
        variant="h4"
        fontWeight="700"
        mt="2.5rem"
        mb="1rem"
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.secondary[100]}, ${theme.palette.secondary[400]})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "0px 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${theme.palette.secondary[500]}20, ${theme.palette.secondary[700]}30)`,
          }}
        >
          <TableChartIcon sx={{ color: theme.palette.secondary[300] }} />
        </Box>
        Rincian Per {grouping.charAt(0).toUpperCase() + grouping.slice(1)}
      </Typography>

      {/* TABLE SECTION */}
      <Box mb="50px">
        <Paper
          elevation={0}
          sx={{
            p: 0,
            overflow: "hidden",
            borderRadius: "20px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.08)",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(145deg, #1e1e1e, #2a2a2a)"
                : "#ffffff",
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 650,
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
                borderRadius: "10px",
              },
            }}
          >
            <Table
              stickyHeader
              sx={{ minWidth: 650 }}
              aria-label="buku warkat table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "700",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                      fontSize: "14px",
                      width: 50,
                      borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                    }}
                  />
                  <TableCell
                    sx={{
                      fontWeight: "700",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                      fontSize: "14px",
                      color: theme.palette.text.secondary,
                      borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                    }}
                  >
                    Periode
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "700",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                      fontSize: "14px",
                      color: theme.palette.text.secondary,
                      borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                    }}
                  >
                    Pemasukan (Realisasi)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "700",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                      fontSize: "14px",
                      color: theme.palette.text.secondary,
                      borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                    }}
                  >
                    Pengeluaran (Realisasi)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "700",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                      fontSize: "14px",
                      color: theme.palette.text.secondary,
                      borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                    }}
                  >
                    Selisih (Net)
                  </TableCell>
                  {grouping === "harian" && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "700",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
                        fontSize: "14px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#eee"}`,
                      }}
                    >
                      Aksi
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((row, index) => {
                  const isOpen = !!expandedRows[index];
                  const hasItems = row.items && row.items.length > 0;
                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        sx={{
                          cursor: hasItems ? "pointer" : "default",
                          transition: "all 0.2s ease",
                          "&:hover": hasItems
                            ? {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(0,0,0,0.02)",
                                transform: "scale(1.002)",
                              }
                            : {},
                        }}
                        onClick={() => {
                          if (hasItems) {
                            setExpandedRows((prev) => ({
                              ...prev,
                              [index]: !prev[index],
                            }));
                          }
                        }}
                      >
                        <TableCell
                          sx={{ width: 50, p: 0, textAlign: "center" }}
                        >
                          {hasItems && (
                            <IconButton
                              size="small"
                              sx={{ color: theme.palette.secondary[200] }}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{
                            color: theme.palette.secondary[100],
                            fontWeight: "500",
                          }}
                        >
                          {row.periode}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: theme.palette.success.main,
                            fontWeight: "500",
                          }}
                        >
                          {formatRupiah(row.pemasukan)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: theme.palette.error.main,
                            fontWeight: "500",
                          }}
                        >
                          {formatRupiah(row.pengeluaran)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              row.pemasukan - row.pengeluaran >= 0
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                            fontWeight: "bold",
                          }}
                        >
                          {formatRupiah(row.pemasukan - row.pengeluaran)}
                        </TableCell>
                        {grouping === "harian" && (
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {moment(row.periode, "DD MMM YYYY").isSame(
                              moment(),
                              "day",
                            ) && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<SyncIcon />}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  textTransform: "none",
                                  backgroundColor: theme.palette.secondary[500],
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.secondary[600],
                                  },
                                }}
                                onClick={() => handleSyncSaldoAwal(row)}
                              >
                                Sinkron Saldo Awal
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Expandable detail row */}
                      {hasItems && (
                        <TableRow>
                          <TableCell
                            sx={{ py: 0, px: 0, border: 0 }}
                            colSpan={grouping === "harian" ? 6 : 5}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  py: 1.5,
                                  px: 3,
                                  backgroundColor:
                                    theme.palette.primary.main + "60",
                                  borderLeft: `3px solid ${theme.palette.secondary[500]}`,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 1,
                                    color: theme.palette.secondary[200],
                                    fontWeight: "bold",
                                  }}
                                >
                                  Detail Transaksi — {row.periode}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        No
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Nama
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Tipe
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Status
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Kode Perkiraan
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Catatan
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          color: theme.palette.secondary[300],
                                          fontWeight: "bold",
                                          fontSize: 12,
                                        }}
                                      >
                                        Nominal
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.items.map((item, i) => (
                                      <TableRow
                                        key={item.id || i}
                                        sx={{
                                          opacity:
                                            item.status === "realisasi"
                                              ? 1
                                              : 0.6,
                                          "&:last-child td": { border: 0 },
                                        }}
                                      >
                                        <TableCell
                                          sx={{
                                            color: theme.palette.secondary[200],
                                            fontSize: 12,
                                          }}
                                        >
                                          {i + 1}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            color: theme.palette.secondary[100],
                                            fontSize: 12,
                                            fontWeight: "500",
                                          }}
                                        >
                                          {item.nama}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={
                                              item.tipe === "pemasukan"
                                                ? "Pemasukan"
                                                : "Pengeluaran"
                                            }
                                            size="small"
                                            sx={{
                                              fontSize: 11,
                                              fontWeight: "bold",
                                              backgroundColor:
                                                item.tipe === "pemasukan"
                                                  ? theme.palette.success.main +
                                                    "25"
                                                  : theme.palette.error.main +
                                                    "25",
                                              color:
                                                item.tipe === "pemasukan"
                                                  ? theme.palette.success.main
                                                  : theme.palette.error.main,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={
                                              item.status === "realisasi"
                                                ? "Realisasi"
                                                : "Alokasi"
                                            }
                                            size="small"
                                            sx={{
                                              fontSize: 11,
                                              fontWeight: "bold",
                                              backgroundColor:
                                                item.status === "realisasi"
                                                  ? theme.palette.info.main +
                                                    "25"
                                                  : theme.palette.warning.main +
                                                    "25",
                                              color:
                                                item.status === "realisasi"
                                                  ? theme.palette.info.main
                                                  : theme.palette.warning.main,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            color: theme.palette.secondary[200],
                                            fontSize: 12,
                                          }}
                                        >
                                          {item.kode_perkiraan
                                            ? `${item.kode_perkiraan} - ${item.nama_perkiraan || ""}`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            color: theme.palette.secondary[200],
                                            fontSize: 12,
                                          }}
                                        >
                                          {item.catatan || "-"}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            color:
                                              item.tipe === "pemasukan"
                                                ? theme.palette.success.main
                                                : theme.palette.error.main,
                                            fontSize: 12,
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {item.tipe === "pengeluaran"
                                            ? "- "
                                            : ""}
                                          {formatRupiah(item.nominal)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* TOTAL / SUMMARY ROW */}
                {currentData.length > 0 && (
                  <TableRow
                    sx={{
                      background:
                        theme.palette.mode === "dark"
                          ? "linear-gradient(90deg, #1a1a2e, #16213e)"
                          : "linear-gradient(90deg, #f8f9fa, #e9ecef)",
                    }}
                  >
                    <TableCell />
                    <TableCell
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: "800",
                        fontSize: 14,
                      }}
                    >
                      TOTAL
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: theme.palette.success.main,
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      {formatRupiah(
                        currentData.reduce((sum, r) => sum + r.pemasukan, 0),
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      {formatRupiah(
                        currentData.reduce((sum, r) => sum + r.pengeluaran, 0),
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          currentData.reduce(
                            (sum, r) => sum + r.pemasukan - r.pengeluaran,
                            0,
                          ) >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      {formatRupiah(
                        currentData.reduce(
                          (sum, r) => sum + r.pemasukan - r.pengeluaran,
                          0,
                        ),
                      )}
                    </TableCell>
                    {grouping === "harian" && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <FloatingAlert
        open={message.length > 0}
        onClose={handleCloseSnackbar}
        message={message}
        severity={status === "failed" ? "error" : status}
      />
    </Box>
  );
};

export default BukuWarkat;
